import { useState, useEffect, useCallback } from 'react'
import { apiService } from '../../../services/api'
import { debug } from '../../../utils/debug'
import { useUser } from '../../../context/UserContext'
import { 
  BACKEND_TO_STANDARD, 
  BACKEND_TO_FRONTEND_CODE,
  OPERATOR_DISPLAY_ORDER,
  DISPLAY_TO_BACKEND,
  getOperatorInfo 
} from './constants'

export const useKAMPerformance = () => {
  const { hasOperatorAccess } = useUser()
  
  // Main data state
  const [loading, setLoading] = useState(false)
  const [error, setError] = useState(null)
  const [kamData, setKamData] = useState([])
  const [dataFreshness, setDataFreshness] = useState({})
  const [freshnessThresholds, setFreshnessThresholds] = useState({ green_hours: 24, yellow_hours: 48 })
  
  // View and filter state
  const [selectedOperator, setSelectedOperator] = useState('Wszyscy')
  const [viewMode, setViewMode] = useState('general') // 'general', 'by-operator', 'per-kam'
  
  // Month/year selection
  const now = new Date()
  const [selectedMonth, setSelectedMonth] = useState(now.getMonth() + 1)
  const [selectedYear, setSelectedYear] = useState(now.getFullYear())
  const [selectedProductGroup, setSelectedProductGroup] = useState(null)
  
  // Per KAM view state
  const [selectedKamName, setSelectedKamName] = useState('')
  const [perKamOperator, setPerKamOperator] = useState('Wszyscy')
  const [timeRangeType, setTimeRangeType] = useState('current')
  const [customStartMonth, setCustomStartMonth] = useState(1)
  const [customStartYear, setCustomStartYear] = useState(now.getFullYear())
  const [customEndMonth, setCustomEndMonth] = useState(now.getMonth() + 1)
  const [customEndYear, setCustomEndYear] = useState(now.getFullYear())
  const [perKamData, setPerKamData] = useState(null)
  const [perKamLoading, setPerKamLoading] = useState(false)
  const [exportLoading, setExportLoading] = useState(false)

  debug.component('KAMPerformance', 'Hook initialized')

  // Calculate date range based on time range type
  const calculateDateRange = useCallback(() => {
    const now = new Date()
    let startMonth, startYear, endMonth, endYear
    
    switch (timeRangeType) {
      case 'current':
        startMonth = now.getMonth() + 1
        startYear = now.getFullYear()
        endMonth = now.getMonth() + 1
        endYear = now.getFullYear()
        break
      case 'previous':
        const prevMonth = now.getMonth() === 0 ? 12 : now.getMonth()
        const prevYear = now.getMonth() === 0 ? now.getFullYear() - 1 : now.getFullYear()
        startMonth = prevMonth
        startYear = prevYear
        endMonth = prevMonth
        endYear = prevYear
        break
      case 'ytd':
        startMonth = 1
        startYear = now.getFullYear()
        endMonth = now.getMonth() + 1
        endYear = now.getFullYear()
        break
      case 'custom':
        startMonth = customStartMonth
        startYear = customStartYear
        endMonth = customEndMonth
        endYear = customEndYear
        break
      default:
        startMonth = now.getMonth() + 1
        startYear = now.getFullYear()
        endMonth = now.getMonth() + 1
        endYear = now.getFullYear()
    }
    
    return { startMonth, startYear, endMonth, endYear }
  }, [timeRangeType, customStartMonth, customStartYear, customEndMonth, customEndYear])

  // Fetch main KAM data
  const fetchData = useCallback(async () => {
    setLoading(true)
    setError(null)
    
    try {
      const dateParams = { month: selectedMonth, year: selectedYear }
      if (selectedProductGroup) dateParams.product_group = selectedProductGroup
      debug.log('Fetching KAM performance data', { dateParams }, 'KAMPerformance')

      // Fetch KAM performance data and data freshness in parallel
      const [performanceResponse, freshnessResponse] = await Promise.all([
        apiService.getKAMPerformance(dateParams),
        apiService.getDataFreshness().catch((err) => {
          debug.warn('Data freshness fetch failed', err, 'KAMPerformance')
          return { data: { operators: {} } }
        })
      ])
      
      const kamList = performanceResponse.data?.data || []
      const freshnessData = freshnessResponse.data?.operators || {}
      const thresholdsData = freshnessResponse.data?.thresholds || { green_hours: 24, yellow_hours: 48 }
      
      setDataFreshness(freshnessData)
      setFreshnessThresholds(thresholdsData)
      
      debug.log('KAM performance data fetched successfully', { 
        performanceResponse: performanceResponse.data,
        kamList,
        kamCount: kamList.length
      }, 'KAMPerformance')
      
      // Filter out KAMs with no data
      const activeKamList = kamList.filter(kam => {
        const hasVisits = kam.total_visits_month > 0 || kam.total_visits_alltime > 0 || 
                         kam.total_visits > 0 || kam.unique_salons_visited > 0
        const hasGoals = kam.goal_value > 0 || kam.achievement_percent > 0
        // !== 0 (not > 0): a negative MTD (returns exceeding sales) must stay
        // visible, otherwise the table no longer reconciles with the operator total
        const hasSales = (kam.actual_sales_mtd || 0) !== 0
        return hasVisits || hasGoals || hasSales
      })
      
      // Filter by operator access
      const accessibleKamList = activeKamList.filter(kam => {
        const standardCode = BACKEND_TO_STANDARD[kam.operator_code] || kam.operator_code
        return hasOperatorAccess(standardCode)
      })
      
      debug.log(`Filtered KAMs: ${kamList.length} -> ${activeKamList.length} -> ${accessibleKamList.length}`, 
        null, 'KAMPerformance')
      setKamData(accessibleKamList)
      
    } catch (err) {
      debug.error('Error fetching KAM performance data', err, 'KAMPerformance')
      setError('Błąd podczas ładowania danych wydajności KAM-ów')
    } finally {
      setLoading(false)
    }
  }, [selectedMonth, selectedYear, selectedProductGroup, hasOperatorAccess])

  // Fetch per-KAM detailed data
  const fetchPerKamData = useCallback(async () => {
    if (!selectedKamName) {
      debug.log('No KAM selected, skipping fetch', null, 'KAMPerformance')
      return
    }
    
    setPerKamLoading(true)
    setError(null)
    
    try {
      const { startMonth, startYear, endMonth, endYear } = calculateDateRange()
      
      const params = {
        kam_name: selectedKamName,
        start_month: startMonth,
        start_year: startYear,
        end_month: endMonth,
        end_year: endYear
      }
      
      if (perKamOperator !== 'Wszyscy') {
        params.operator_code = DISPLAY_TO_BACKEND[perKamOperator] || perKamOperator
      }
      
      const response = await apiService.getKAMPerformanceIndividual(params)
      const data = response.data?.data || []
      
      debug.log('Individual KAM performance data fetched', { dataCount: data.length }, 'KAMPerformance')
      setPerKamData(data)
      
    } catch (err) {
      debug.error('Error fetching individual KAM performance', err, 'KAMPerformance')
      setError('Błąd podczas ładowania danych wydajności KAMa')
    } finally {
      setPerKamLoading(false)
    }
  }, [selectedKamName, perKamOperator, calculateDateRange])

  // Export handlers
  const handleExport = useCallback(async () => {
    try {
      debug.log('Exporting KAM performance data', { month: selectedMonth, year: selectedYear }, 'KAMPerformance')
      
      const { blob, filename } = await apiService.exportKAMPerformance({
        month: selectedMonth,
        year: selectedYear
      })
      
      const url = window.URL.createObjectURL(blob)
      const link = document.createElement('a')
      link.href = url
      link.download = filename
      document.body.appendChild(link)
      link.click()
      document.body.removeChild(link)
      window.URL.revokeObjectURL(url)
      
      debug.log('Export successful', { filename }, 'KAMPerformance')
    } catch (err) {
      debug.error('Error exporting KAM performance data', err, 'KAMPerformance')
      setError('Błąd podczas eksportu danych')
    }
  }, [selectedMonth, selectedYear])

  const handlePerKamExport = useCallback(async () => {
    if (!selectedKamName) {
      setError('Wybierz KAMa aby eksportować dane')
      return
    }
    
    setExportLoading(true)
    setError(null)
    
    try {
      const { startMonth, startYear, endMonth, endYear } = calculateDateRange()
      
      const params = {
        kam_name: selectedKamName,
        start_month: startMonth,
        start_year: startYear,
        end_month: endMonth,
        end_year: endYear
      }
      
      if (perKamOperator !== 'Wszyscy') {
        params.operator_code = DISPLAY_TO_BACKEND[perKamOperator] || perKamOperator
      }
      
      const { blob, filename } = await apiService.exportKAMPerformanceIndividual(params)
      
      const url = window.URL.createObjectURL(blob)
      const link = document.createElement('a')
      link.href = url
      link.download = filename
      document.body.appendChild(link)
      link.click()
      document.body.removeChild(link)
      window.URL.revokeObjectURL(url)
      
      debug.log('Export successful', { filename }, 'KAMPerformance')
    } catch (err) {
      debug.error('Error exporting individual KAM performance', err, 'KAMPerformance')
      setError('Błąd podczas eksportu danych. Spróbuj ponownie.')
    } finally {
      setExportLoading(false)
    }
  }, [selectedKamName, perKamOperator, calculateDateRange])

  // Get unique KAM names
  const getUniqueKamNames = useCallback(() => {
    if (!kamData || kamData.length === 0) return []
    
    let filteredKams = kamData
    
    if (perKamOperator !== 'Wszyscy') {
      const backendCode = DISPLAY_TO_BACKEND[perKamOperator]
      filteredKams = kamData.filter(kam => {
        const standardCode = BACKEND_TO_STANDARD[kam.operator_code] || kam.operator_code
        return standardCode === backendCode
      })
    }
    
    return [...new Set(filteredKams.map(kam => kam.kam_name))].sort()
  }, [kamData, perKamOperator])

  // Get available operators for filter
  const getOperatorOptions = useCallback(() => {
    const accessibleCodes = OPERATOR_DISPLAY_ORDER.filter(opCode => {
      const backendToStandard = { 'OPL': 'ORANGE', 'TMPL': 'TMOBILE', 'PLK': 'PLUS', 'P4': 'P4' }
      return hasOperatorAccess(backendToStandard[opCode] || opCode)
    })
    
    return ['Wszyscy', ...accessibleCodes.map(opCode => getOperatorInfo(opCode).display_name)]
  }, [hasOperatorAccess])

  // Filter KAMs by operator
  const getFilteredKamData = useCallback(() => {
    if (selectedOperator === 'Wszyscy') return kamData
    
    return kamData.filter(kam => {
      const operatorInfo = getOperatorInfo(kam.operator_code)
      return operatorInfo.display_name === selectedOperator
    })
  }, [kamData, selectedOperator])

  // Group KAMs by operator
  const getKamsByOperator = useCallback(() => {
    const grouped = {}
    
    OPERATOR_DISPLAY_ORDER.forEach(opCode => {
      const operatorInfo = getOperatorInfo(opCode)
      // Check if user has access
      const backendToStandard = { 'OPL': 'ORANGE', 'TMPL': 'TMOBILE', 'PLK': 'PLUS', 'P4': 'P4' }
      if (!hasOperatorAccess(backendToStandard[opCode] || opCode)) return
      
      grouped[opCode] = {
        code: opCode,
        name: operatorInfo.display_name,
        color: operatorInfo.color,
        bgLight: operatorInfo.bgLight,
        bgHover: operatorInfo.bgHover,
        borderColor: operatorInfo.borderColor,
        textColor: operatorInfo.textColor,
        kams: []
      }
    })

    kamData.forEach(kam => {
      const backendCode = kam.operator_code
      const frontendCode = BACKEND_TO_FRONTEND_CODE[backendCode] || backendCode || 'OPL'
      
      if (grouped[frontendCode]) {
        grouped[frontendCode].kams.push(kam)
      }
    })
    
    // Calculate summary for each operator.
    // The synthetic "Nieprzypisane" row carries sales no KAM made (WWW/TELE/magazyn) —
    // it must NOT count into the KAM totals; it is reported separately as unassignedSales.
    Object.values(grouped).forEach(group => {
      const realKams = group.kams.filter(kam => !kam.is_unassigned)
      group.totalGoal = realKams.reduce((sum, kam) => sum + (kam.goal_value || 0), 0)
      group.totalGoalTarget = realKams.reduce((sum, kam) => sum + (kam.goal_value_target || kam.goal_value || 0), 0)
      group.totalSales = realKams.reduce((sum, kam) => sum + (kam.actual_sales_mtd || 0), 0)
      group.unassignedSales = group.kams
        .filter(kam => kam.is_unassigned)
        .reduce((sum, kam) => sum + (kam.actual_sales_mtd || 0), 0)
      group.achievementPercent = group.totalGoal > 0
        ? (group.totalSales / group.totalGoal * 100)
        : 0
      
      // Sort KAMs by forecast descending; the synthetic "Nieprzypisane" row always last
      group.kams.sort((a, b) => {
        if (!!a.is_unassigned !== !!b.is_unassigned) return a.is_unassigned ? 1 : -1
        const forecastA = a.forecast_vs_goal_percent ?? a.achievement_percent ?? 0
        const forecastB = b.forecast_vs_goal_percent ?? b.achievement_percent ?? 0
        return forecastB - forecastA
      })
    })

    return grouped
  }, [kamData, hasOperatorAccess])

  // Build goal display
  const buildGoalDisplay = useCallback((kam) => {
    const hasGoal = (kam.goal_value && kam.goal_value > 0) || 
                    (kam.achievement_percent !== null && kam.achievement_percent !== undefined)
    
    if (!hasGoal) {
      return { text: 'Brak celów', type: 'default' }
    }
    
    const forecastVsGoalPercent = kam.forecast_vs_goal_percent
    const achievementPercent = kam.achievement_percent || 0
    
    if (forecastVsGoalPercent !== null && forecastVsGoalPercent !== undefined) {
      if (forecastVsGoalPercent >= 100) {
        return { text: `${forecastVsGoalPercent.toFixed(1)}% (prog.)`, type: 'success' }
      } else if (forecastVsGoalPercent >= 80) {
        return { text: `${forecastVsGoalPercent.toFixed(1)}% (prog.)`, type: 'info' }
      } else if (forecastVsGoalPercent >= 60) {
        return { text: `${forecastVsGoalPercent.toFixed(1)}% (prog.)`, type: 'warning' }
      } else {
        return { text: `${forecastVsGoalPercent.toFixed(1)}% (prog.)`, type: 'error' }
      }
    } else if (achievementPercent !== null && achievementPercent !== undefined) {
      return { text: `${achievementPercent.toFixed(1)}% MTD`, type: 'info' }
    }
    
    return { text: 'Brak danych', type: 'default' }
  }, [])

  // Initial data fetch
  useEffect(() => {
    fetchData()
  }, [fetchData])

  return {
    // State
    loading,
    error,
    kamData,
    dataFreshness,
    freshnessThresholds,
    viewMode,
    selectedOperator,
    selectedMonth,
    selectedYear,
    selectedProductGroup,
    selectedKamName,
    perKamOperator,
    timeRangeType,
    customStartMonth,
    customStartYear,
    customEndMonth,
    customEndYear,
    perKamData,
    perKamLoading,
    exportLoading,
    
    // Setters
    setViewMode,
    setSelectedOperator,
    setSelectedMonth,
    setSelectedYear,
    setSelectedProductGroup,
    setSelectedKamName,
    setPerKamOperator,
    setTimeRangeType,
    setCustomStartMonth,
    setCustomStartYear,
    setCustomEndMonth,
    setCustomEndYear,
    setError,
    
    // Actions
    fetchData,
    fetchPerKamData,
    handleExport,
    handlePerKamExport,
    
    // Computed
    getUniqueKamNames,
    getOperatorOptions,
    getFilteredKamData,
    getKamsByOperator,
    buildGoalDisplay
  }
}

export default useKAMPerformance


